import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';

import { publicApi } from '@/lib/api/publicClient';
import { useAuthStore } from '@/lib/auth/authStore';
import { useT } from '@/lib/i18n/useT';

interface ExchangeResponse {
  access_token: string;
  stationId: number;
  expiresInSec: number;
  user: {
    id: number;
    email: string;
    firstname?: string;
    lastname?: string;
    companyId: number | null;
  };
}

/**
 * Caso 11 v4 — Operator session entry. Reads the raw `stationToken` from
 * the query string, exchanges it via the public endpoint, stores the
 * resulting JWT in authStore, and redirects to the operator station view.
 *
 * Public route — anyone with the URL gets a session, that's the whole
 * point. Don't add a guard here.
 */
export function OperatorLoginPage() {
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setSession = useAuthStore((s) => s.setSession);
  const [error, setError] = useState<string | null>(null);
  // useRef-based one-shot guard so React 18's StrictMode double-invoke
  // doesn't consume the token twice (the second call would fail with
  // "lastUsedAt updates fine but the row is still valid" — harmless, but
  // noisy in dev logs).
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const stationToken = params.get('stationToken');
    if (!stationToken) {
      setError(t('operatorLogin.missingToken'));
      return;
    }
    (async () => {
      try {
        const res = await publicApi.post<ExchangeResponse>(
          `/xk-station/auth/exchange`,
          { stationToken },
        );
        setSession({
          token: res.access_token,
          user: {
            id: res.user.id,
            email: res.user.email,
            firstname: res.user.firstname,
            lastname: res.user.lastname,
            companyId: res.user.companyId,
            // The operator session inherits the minting user's identity;
            // restriction is enforced server-side via the `app: 'operator'`
            // JWT claim, not a frontend role string.
            role: 'staff',
          } as never,
          role: 'staff',
        });
        navigate(`/operator/${res.stationId}`, { replace: true });
      } catch (e: any) {
        setError(e?.message ?? t('operatorLogin.exchangeFailed'));
      }
    })();
  }, [params, navigate, setSession, t]);

  return (
    <div className="grid min-h-screen place-items-center bg-[var(--color-bg-primary,#000)] p-4 text-white">
      <div className="max-w-sm text-center">
        {error ? (
          <>
            <AlertCircle className="mx-auto mb-3 h-10 w-10 text-[var(--color-error)]" />
            <h1 className="text-lg font-bold">
              {t('operatorLogin.errorTitle')}
            </h1>
            <p className="mt-2 text-sm opacity-80">{error}</p>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto mb-3 h-10 w-10 animate-spin text-[var(--color-accent-ink)]" />
            <h1 className="text-lg font-bold">{t('operatorLogin.title')}</h1>
            <p className="mt-2 text-sm opacity-80">
              {t('operatorLogin.subtitle')}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
